// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { API_BASE } from '../utils/api'; // adjust the relative path if your folders differ

const AuthContext = createContext(null);

// --- Cognito runtime config (from window or env) ---
const AUTH_PROVIDER = window.__AUTH_PROVIDER__ || process.env.REACT_APP_AUTH_PROVIDER || 'local';
const COG_HOSTED   = window.__COGNITO_HOSTED_UI__ || process.env.REACT_APP_COGNITO_HOSTED_UI || '';
const COG_CLIENT   = window.__COGNITO_CLIENT_ID__ || process.env.REACT_APP_COGNITO_CLIENT_ID || '';

// --- Tiny PKCE helpers ---
function b64url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/,'');
}
async function sha256(s) {
  const enc = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest('SHA-256', enc);
  return b64url(digest);
}

export function useAuth() {
  return useContext(AuthContext);
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u) return null;
    // normalize id for the rest of the app
    return { ...u, id: u.id || u._id };
  } catch {
    return null;
  }
}

function isPlaceholderUsername(name) {
  if (!name) return true;            // treat empty as needing a username
  return /^user_[a-z0-9]{6,}$/i.test(name);
}

function getAnyToken() {
  // Prefer ACCESS token; fall back to ID if needed
  return (
    localStorage.getItem('rv_access_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('rv_id_token') ||
    localStorage.getItem('id_token') ||
    ''
  );
}

function getAccessToken() {
  return (
    localStorage.getItem('rv_access_token') ||
    localStorage.getItem('access_token') ||
    ''
  );
}

function getIdToken() {
  // ID token is still useful for reading profile-type info (email), but
  // BACKEND AUTH now uses ACCESS tokens.
  return (
    localStorage.getItem('rv_id_token') ||
    localStorage.getItem('id_token') ||
    ''
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsUsername, setNeedsUsername] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const refreshTimerRef = useRef(null);

  // Tiny JWT decoder (no lib)
  function decodeJwt(token) {
    try {
      const [, payload] = token.split('.');
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  function isTokenExpired(token) {
    const claims = decodeJwt(token);
    if (!claims || !claims.exp) return true;
    return claims.exp < Math.floor(Date.now() / 1000);
  }

  function clearAllTokens() {
    localStorage.removeItem('rv_id_token');
    localStorage.removeItem('rv_access_token');
    localStorage.removeItem('rv_refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
  }

  function redirectToCognitoLogout() {
    if (AUTH_PROVIDER === 'cognito' && COG_HOSTED && COG_CLIENT) {
      const logoutUri = encodeURIComponent(`${window.location.origin}/logout`);
      window.location.href = `${COG_HOSTED}/logout?client_id=${COG_CLIENT}&logout_uri=${logoutUri}`;
    } else {
      window.location.replace('/');
    }
  }

  async function tryRefreshToken() {
    if (AUTH_PROVIDER !== 'cognito') return false;
    const refreshToken = localStorage.getItem('rv_refresh_token');
    if (!refreshToken) return false;
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: COG_CLIENT,
        refresh_token: refreshToken,
      });
      const resp = await fetch(`${COG_HOSTED}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!resp.ok) return false;
      const tokens = await resp.json();
      if (tokens.access_token) {
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('rv_access_token', tokens.access_token);
      }
      if (tokens.id_token) {
        localStorage.setItem('id_token', tokens.id_token);
        localStorage.setItem('rv_id_token', tokens.id_token);
      }
      return true;
    } catch (e) {
      console.warn('[AUTH] Silent refresh failed', e);
      return false;
    }
  }

  function scheduleProactiveRefresh(token) {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const claims = decodeJwt(token);
    if (!claims?.exp) return;
    // Fire 5 minutes before expiry
    const msUntilRefresh = (claims.exp - Math.floor(Date.now() / 1000) - 300) * 1000;
    if (msUntilRefresh <= 0) {
      // Less than 5 min left - refresh immediately
      performProactiveRefresh();
      return;
    }
    console.log('[AUTH] Proactive refresh scheduled in', Math.round(msUntilRefresh / 60000), 'min');
    refreshTimerRef.current = setTimeout(performProactiveRefresh, msUntilRefresh);
  }

  async function performProactiveRefresh(attempt = 1) {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 5000; // 5 seconds between retries
    console.log(`[AUTH] Proactive token refresh running (attempt ${attempt}/${MAX_RETRIES})...`);
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const newToken = getAccessToken();
      console.log('[AUTH] Proactive refresh succeeded');
      scheduleProactiveRefresh(newToken); // reschedule for the new token's expiry
    } else if (attempt < MAX_RETRIES) {
      console.log(`[AUTH] Refresh attempt ${attempt} failed — retrying in ${RETRY_DELAY_MS / 1000}s...`);
      refreshTimerRef.current = setTimeout(() => performProactiveRefresh(attempt + 1), RETRY_DELAY_MS);
    } else {
      // All retries exhausted - session truly over
      console.log('[AUTH] Proactive refresh failed after all retries - clearing session');
      clearAllTokens();
      setUser(null);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      redirectToCognitoLogout();
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const authCode = params.get('code');

        // Let the /callback effect handle the code exchange
        if (authCode) {
          setAuthReady(true);
          setLoading(false);
          return;
        }

        let existing = getAccessToken();

        // No token at all - not logged in
        if (!existing) {
          setAuthReady(true);
          setLoading(false);
          return;
        }

        // Token is expired - try a silent refresh before giving up
        if (isTokenExpired(existing)) {
          console.log('[AUTH] Access token expired, attempting silent refresh...');
          const refreshed = await tryRefreshToken();
          if (!refreshed) {
            // Refresh token also dead - truly logged out
            console.log('[AUTH] Refresh failed, clearing session');
            clearAllTokens();
            setUser(null);
            setAuthReady(true);
            setLoading(false);
            return;
          }
          existing = getAccessToken();
          console.log('[AUTH] Silent refresh succeeded');
        }

        // Token is valid - user is authenticated.
        // user is already set from readStoredUser() in useState.
        // Try to enrich from /api/me but NEVER clear tokens if it fails -
        // a backend hiccup is not a logout.
        try {
          const res = await fetch(`${API_BASE}/api/me`, {
            headers: { Authorization: `Bearer ${existing}` }
          });
          if (res.ok) {
            const me = await res.json();
            const u = me?.user ?? me;
            if (u && (u.id || u._id)) {
              setUser({
                id: u.id || u._id,
                roles: u.roles || [],
                role: u.role || (u.roles && u.roles[0]) || 'reader',
                username: u.username || '',
              });
              setNeedsUsername(isPlaceholderUsername(u.username));
            }
          }
          // If /api/me returns 4xx/5xx keep the user logged in.
          // The Cognito token is the source of truth, not the backend.
        } catch (e) {
          console.warn('[AUTH] /api/me enrichment failed - using cached user data', e);
        }

        // Schedule proactive refresh so token never expires while tab is open
        scheduleProactiveRefresh(existing);

      } catch (e) {
        // DO NOT clear tokens here - a JS error is not a logout.
        console.error('[AUTH] Unexpected init error:', e);
      } finally {
        setAuthReady(true);
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Listen for 401 signals fired by the api.js helpers
  useEffect(() => {
    const handler = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      clearAllTokens();
      setUser(null);
      setNeedsUsername(false);
      redirectToCognitoLogout();
    };
    window.addEventListener('rv:unauthorized', handler);
    return () => window.removeEventListener('rv:unauthorized', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the tab regains focus, check token health and refresh if needed
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const token = getAccessToken();
      if (!token) return; // not logged in

      if (isTokenExpired(token)) {
        console.log('[AUTH] Tab resumed with expired token — refreshing...');
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          const newToken = getAccessToken();
          console.log('[AUTH] Visibility refresh succeeded');
          scheduleProactiveRefresh(newToken);
        } else {
          console.log('[AUTH] Visibility refresh failed — clearing session');
          clearAllTokens();
          setUser(null);
          redirectToCognitoLogout();
        }
      } else {
        // Token still valid — make sure the proactive timer is running
        scheduleProactiveRefresh(token);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep localStorage in sync
  useEffect(() => {
    if (user) {
      const normalized = { ...user, id: user.id || user._id };
      localStorage.setItem('user', JSON.stringify(normalized));
      // optional legacy key used elsewhere in the app
      if (normalized.id) {
        localStorage.setItem('user_id', normalized.id);
      }
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('user_id');
    }
  }, [user]);

  useEffect(() => {
  if (AUTH_PROVIDER !== 'cognito') return;
  if (window.location.pathname !== '/callback') return;

  let ran = false;

  (async () => {
    if (ran) return; ran = true;

    try {
      const code = new URLSearchParams(window.location.search).get('code');
      const ver  = sessionStorage.getItem('pkce_verifier');
      if (!code || !ver) throw new Error('Missing code or PKCE verifier');

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COG_CLIENT,
        code_verifier: ver,
        code,
        redirect_uri: `${window.location.origin}/callback`,
      });

      const resp = await fetch(`${COG_HOSTED}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });

      const text = await resp.text();
      if (!resp.ok) {
        throw new Error(JSON.parse(text)?.error_description || 'OAuth exchange failed');
      }
      const tokens = JSON.parse(text);

      if (tokens.id_token) {
        localStorage.setItem('id_token', tokens.id_token);
        localStorage.setItem('rv_id_token', tokens.id_token);
      }
      if (tokens.access_token) {
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('rv_access_token', tokens.access_token);
      }
      if (tokens.refresh_token) {
        localStorage.setItem('rv_refresh_token', tokens.refresh_token);
      }

      // Schedule proactive refresh for the new access token
      if (tokens.access_token) scheduleProactiveRefresh(tokens.access_token);

      // Hydrate user from /api/me using the ACCESS token
      try {
        const meResp = await fetch(`${API_BASE}/api/me`, {
          headers: { Authorization: `Bearer ${tokens.access_token}` }
        });
        if (meResp.ok) {
          const me = await meResp.json();
          const u = me?.user ?? me;
          if (u && (u.id || u._id)) {
            const idt = tokens.id_token || getIdToken();
            const claims = idt ? decodeJwt(idt) : null;

            setUser({
              id: u.id || u._id,
              roles: u.roles || [],
              role: u.role || (u.roles && u.roles[0]) || 'reader',
              username: u.username || '',
              sub: claims?.sub,
              email: claims?.email || claims?.['cognito:username'] || '',
            });
            setNeedsUsername(isPlaceholderUsername(u.username));
          }
        }
      } catch {}

      // Clean URL so the code cannot be reused.
      // Default is the signed-in Home, not /dashboard: Dashboard is role-based
      // work panels, so an author with no assignments landed on an empty page.
      // postLoginFrom still wins — QuotePage sets it to resume a parked quote.
      const from = sessionStorage.getItem('postLoginFrom') || '/home';
      sessionStorage.removeItem('postLoginFrom');
      window.location.href = from;
    } catch (e) {
      console.error('OAuth callback failed', e);
      localStorage.removeItem('rv_id_token');
      localStorage.removeItem('rv_access_token');
    }finally {
      setAuthReady(true); // ← signal guards that auth init (callback path) is done
    }
  })();
}, []);


   // Centralized Cognito redirect (login or signup) with PKCE
async function startCognito(mode = 'login') {
  if (AUTH_PROVIDER !== 'cognito') {
    console.warn('[AUTH] startCognito called but AUTH_PROVIDER != cognito');
    return { redirecting: false };
  }

  try {
    // Generate verifier
    const verBytes = crypto.getRandomValues(new Uint8Array(32));
    const verifier = Array.from(verBytes).map(b => ('0' + b.toString(16)).slice(-2)).join('');
    sessionStorage.setItem('pkce_verifier', verifier);

    // Try S256 challenge first; fall back to plain if anything fails
    let challenge = '';
    let method = 'plain';
    try {
      challenge = await sha256(verifier);
      method = 'S256';
    } catch (e) {
      console.warn('[AUTH] sha256 failed, falling back to plain PKCE:', e);
      challenge = verifier;
      method = 'plain';
    }

    const qs = new URLSearchParams({
      client_id: COG_CLIENT,
      response_type: 'code',
      code_challenge_method: method,
      code_challenge: challenge,
      redirect_uri: `${window.location.origin}/callback`,
      scope: 'openid email profile'
    });

    const authUrl = `${COG_HOSTED}/oauth2/authorize?${qs.toString()}`;
    // Helpful runtime breadcrumb
    window.__last_auth_url = authUrl;
    console.log('[AUTH] redirecting to Hosted UI →', authUrl);

    window.location.href = authUrl; // use href for full replace
    return { redirecting: true };
  } catch (e) {
    console.error('[AUTH] startCognito error:', e);
    return { redirecting: false, error: String(e) };
  }
}

  // PATCH /api/user/username
  async function updateUsername(newUsername) {
    const token = getAccessToken();
    if (!token) return { success: false, error: 'Not authenticated' };
    try {
      const resp = await fetch(`${API_BASE}/api/user/username`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username: newUsername }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        return { success: false, error: data?.error || 'Failed to update username' };
      }
      if (data?.user) {
        setUser((prev) => ({ ...(prev || {}), ...data.user }));
        setNeedsUsername(false);
      }
      return { success: true, user: data?.user };
    } catch {
      return { success: false, error: 'Network error' };
    }
  }



  async function login() {
    setError('');
    setLoading(true);
    try {
      // Cognito Hosted UI is the only auth path.
      await startCognito('login');
      return { success: false, redirecting: true };
    } catch (e) {
      const msg = 'Network error while logging in';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }


  async function logout() {
    setError('');
    setLoading(true);
    try {
      // Cognito is the only auth path; no backend session to clear.
    } finally {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      localStorage.removeItem('rv_id_token');
      localStorage.removeItem('rv_access_token');
      localStorage.removeItem('rv_refresh_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      localStorage.removeItem('user_id');
      setUser(null);
      setNeedsUsername(false);
      setLoading(false);
      redirectToCognitoLogout();
    }
  }

  const value = {
    user,
    setUser,     // keep exposed if other code sets it directly
    loading,
    error,
    setError,    // expose to clear inline messages
    login,
    logout,
    startCognito,
    // username flow
    needsUsername,
    setNeedsUsername,
    updateUsername,
    authReady, 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
